import type { EditorV2HostApi } from "@docit/editor-react";
import type { Editor } from "@tiptap/react";
import { applyAiResult, selectionOffsets } from "~/features/documents/editor/applyAiResult";
import type {
  ApplyOptions,
  DocumentActionsPort,
  DocumentSelectionPort,
} from "~/features/documents/editor/ports";

/**
 * The two editor ports, bound to a live editor (slate-feature-transfer W8 §D.0, §F.2; the v2
 * lane added by W6.1).
 *
 * W8a declared the ports and shipped the honest absences; W8b bound them to the **Tiptap
 * lane**, because that was the lane whose editor instance slate actually holds:
 * `DocumentEditor` hands its `Editor` back through `onEditorReady`, and
 * `applyAiResult`/`selectionOffsets` (W6, `packages/docit-docs`) already know how to read a
 * selection and splice a markdown fragment through the editor's own parser.
 *
 * ── The v2 lane, since W6.1 ──────────────────────────────────────────────────────────────
 * `@docit/editor-react` used to expose exactly one thing to its host — `insertObjects` — so
 * the v2 lane had no port surface at all and every write affordance rendered disabled. That
 * gap is closed in the OWNER package rather than worked around here: `EditorV2HostApi` now
 * carries `getSelection` / `applyAtRange` / `documentEnd` / `findText`, each backed by the
 * engine handle the package still keeps private (no ProseMirror or Yjs type crosses the
 * seam). `createV2Ports` is the thin mapping from that api to these ports, so the consumers —
 * the S6–S8 panels, the comment-thread transform, the skill cards — change nothing at all and
 * cannot tell which lane they are writing into.
 *
 * Two honest differences remain, and they are stated where they bite rather than hidden:
 * a v2 write lands as attributed PROVISIONAL text through the engine's single write path
 * (markdown block syntax is not parsed — editor-core has a markdown projection but no
 * markdown parser), and `parseMarkdown`/`findNodeByAttr` answer null because the v2 engine has
 * neither. Neither is reached by a W8 consumer: slate's `documents.create` is markdown-in, and
 * the Executive Digest is a whole-document replace by design (§B.7).
 *
 * On both lanes the same rule applies in the small: reading a selection works in read mode,
 * but WRITING needs an editable document, so `canWrite` is false otherwise and the apply
 * cards say why.
 */

export type EditorLane = "v1" | "v2";

export interface DocumentPorts {
  selection: DocumentSelectionPort | null;
  actions: DocumentActionsPort | null;
  /**
   * Re-locate a previously anchored span by its TEXT. This is what S10 uses instead of an
   * anchor mark: the thread stores the text it was made from, and if that text has moved the
   * range is found again; if it has changed, the apply refuses with
   * "Selection changed. Please reselect the text." rather than writing over the wrong words.
   */
  findText: ((text: string) => { from: number; to: number } | null) | null;
  /** Why the ports are absent or read-only, in the reader's own words. `null` when usable. */
  unavailableReason: string | null;
}

/** The state every consumer must render honestly: no ports at all. */
export const ABSENT_PORTS: DocumentPorts = {
  selection: null,
  actions: null,
  findText: null,
  unavailableReason: null,
};

/** 400 characters each side — the window a conversation anchor stores (§B.5). */
const CONTEXT_WINDOW = 400;

/**
 * Plain-text offset → ProseMirror position, by binary search on `textBetween`.
 *
 * `textBetween(0, p)` is monotonic in `p`, and using the SAME call `selectionOffsets` uses is
 * what makes the two agree: a walk over text nodes would have to re-derive the block-separator
 * rule and would drift from it the first time an extension changed the schema.
 */
function positionForOffset(editor: Editor, offset: number): number {
  const { doc } = editor.state;
  let low = 0;
  let high = doc.content.size;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (doc.textBetween(0, middle, "\n", "\n").length < offset) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low;
}

/** Markdown → doc JSON, through the editor's OWN manager, so the schema is this editor's. */
function parseMarkdownJson(editor: Editor, markdown: string): unknown {
  const storage = (editor.storage as unknown as Record<string, unknown>).markdown as
    | { manager?: { parse?: (input: string) => unknown } }
    | undefined;
  return storage?.manager?.parse?.(markdown) ?? null;
}

export function createTiptapPorts(editor: Editor, options: { canWrite: boolean }): DocumentPorts {
  const selection: DocumentSelectionPort = {
    getSelection: () => {
      const offsets = selectionOffsets(editor);
      if (!offsets) {
        return null;
      }
      const { doc } = editor.state;
      const before = doc.textBetween(0, offsets.from, "\n", "\n");
      const after = doc.textBetween(offsets.to, doc.content.size, "\n", "\n");
      const parent = editor.state.selection.$from.parent;
      return {
        text: offsets.text,
        from: offsets.from,
        to: offsets.to,
        startOffset: offsets.startOffset,
        endOffset: offsets.endOffset,
        contextBefore: before.slice(-CONTEXT_WINDOW),
        contextAfter: after.slice(0, CONTEXT_WINDOW),
        parentType: parent.type.name,
        parentAttrs: { ...parent.attrs },
      };
    },
  };

  const actions: DocumentActionsPort = {
    canWrite: options.canWrite,
    applyAtRange: async (markdown: string, range, applyOptions: ApplyOptions) => {
      if (!options.canWrite) {
        return false;
      }
      const end = editor.state.doc.content.size;
      return applyAiResult(
        editor,
        markdown,
        range ?? { from: end, to: end },
        applyOptions.insertOnly ? { insertOnly: true } : {},
      );
    },
    parseMarkdown: (markdown: string) => parseMarkdownJson(editor, markdown),
    findNodeByAttr: (type: string, attr: string, value: string) => {
      let found: { pos: number } | null = null;
      editor.state.doc.descendants((node, pos) => {
        if (found) {
          return false;
        }
        if (node.type.name === type && String(node.attrs[attr] ?? "") === value) {
          found = { pos };
          return false;
        }
        return true;
      });
      return found;
    },
    documentEnd: () => editor.state.doc.content.size,
  };

  const findText = (text: string): { from: number; to: number } | null => {
    const needle = text.trim();
    if (needle.length === 0) {
      return null;
    }
    const { doc } = editor.state;
    const flat = doc.textBetween(0, doc.content.size, "\n", "\n");
    const index = flat.indexOf(needle);
    if (index < 0) {
      return null;
    }
    return {
      from: positionForOffset(editor, index),
      to: positionForOffset(editor, index + needle.length),
    };
  };

  return { selection, actions, findText, unavailableReason: null };
}

/**
 * The same two ports over the v2 lane's host api (W6.1).
 *
 * The mapping is deliberately thin: every decision that could differ between the lanes was
 * made once, inside the package, so this file cannot become the place where the two lanes
 * quietly drift. What it DOES own is the shape translation — the engine's selection anchor is
 * a flat record with a `partId`, while a port selection carries `parentAttrs`, because the
 * classic lane's parent node has arbitrary attributes and the v2 lane's has exactly one that
 * matters.
 *
 * `canWrite` is the caller's answer to "may this person change this document", not the
 * editor's: the v2 room independently forces a socket read-only for a user the server denies
 * `update`, so this gate is chrome, never the security boundary.
 */
export function createV2Ports(
  host: EditorV2HostApi,
  options: { canWrite: boolean },
): DocumentPorts {
  const selection: DocumentSelectionPort = {
    getSelection: () => {
      const anchor = host.getSelection();
      if (!anchor) {
        return null;
      }
      return {
        text: anchor.text,
        from: anchor.from,
        to: anchor.to,
        startOffset: anchor.startOffset,
        endOffset: anchor.endOffset,
        contextBefore: anchor.contextBefore,
        contextAfter: anchor.contextAfter,
        parentType: anchor.parentType,
        // The v2 schema declares `partId` on every block; the engine reports it (or null when
        // the block never had one — a v2 document that was not copied forward from v1).
        parentAttrs: anchor.partId === null ? {} : { partId: anchor.partId },
      };
    },
  };

  const actions: DocumentActionsPort = {
    canWrite: options.canWrite,
    applyAtRange: async (markdown: string, range, applyOptions: ApplyOptions) => {
      if (!options.canWrite) {
        return false;
      }
      const outcome = host.applyAtRange(range, markdown, {
        ...(applyOptions.insertOnly ? { insertOnly: true } : {}),
        // The v2 lane makes authorship VISIBLE in the document rather than only in a log:
        // the write is attributed to the agent that produced it and stays provisional until
        // someone stands behind it.
        author: {
          kind: "ai",
          id: `ai:${applyOptions.author.agent}`,
          displayName: applyOptions.author.agent,
        },
      });
      // A proposal is not a refusal, but it is also NOT in the document — and this port's
      // contract is "did the text land". The reviewable copy is visible in the editor itself.
      return outcome.applied;
    },
    // The v2 engine has no markdown PARSER (only a markdown projection), so there is no doc
    // JSON to hand back. No W8 consumer needs one: slate's `documents.create` is markdown-in.
    parseMarkdown: () => null,
    // Node lookup by attribute is a v1 affordance for the digest's anchor node; the digest is
    // a whole-document replace on both lanes (§B.7 deviation 5), so nothing calls this.
    findNodeByAttr: () => null,
    documentEnd: () => host.documentEnd(),
  };

  return { selection, actions, findText: (text) => host.findText(text), unavailableReason: null };
}

/**
 * The ports as the document scene should hand them down.
 *
 * Four states, each with its own sentence, because "the button is greyed out" is not an
 * answer: a v2 document with a live editor has both ports; a v2 document whose editor is not
 * mounted (no collaboration token — the host refuses to show an editor that would discard
 * every keystroke) has neither; a Tiptap document being READ has a selection but nowhere to
 * write; a Tiptap document being EDITED has both.
 */
export function documentPorts(input: {
  editor: Editor | null;
  lane: EditorLane;
  editable: boolean;
  canUpdate: boolean;
  /**
   * The v2 editor's host api, from `onReady`. Null on the classic lane, and null on the v2
   * lane until the editor mounts — which on this lane is a real state: with no collaboration
   * token the host shows the last projected text read-only and never mounts an editor at all.
   */
  v2Host?: EditorV2HostApi | null;
}): DocumentPorts {
  if (input.lane === "v2") {
    if (!input.v2Host) {
      return {
        ...ABSENT_PORTS,
        unavailableReason:
          "The new editor isn't connected on this host, so there is nowhere to apply changes — open this document in the classic editor.",
      };
    }
    const v2 = createV2Ports(input.v2Host, { canWrite: input.canUpdate });
    return input.canUpdate
      ? v2
      : { ...v2, unavailableReason: "You don't have edit access to this document." };
  }
  if (!input.editor) {
    return { ...ABSENT_PORTS, unavailableReason: "The document isn't ready to edit." };
  }
  const canWrite = input.editable && input.canUpdate;
  const ports = createTiptapPorts(input.editor, { canWrite });
  if (canWrite) {
    return ports;
  }
  return {
    ...ports,
    unavailableReason: input.canUpdate
      ? "Open the document for editing to apply changes."
      : "You don't have edit access to this document.",
  };
}
